import { useMemo, useState } from 'react'
import cx from 'classnames'
import {
  useKeenSlider,
  KeenSliderOptions,
  KeenSliderPlugin,
} from 'keen-slider/react'
import { deviceType } from 'detect-it'
import { screens } from '@lib/theme'
import {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
} from '@components/buttons/button'
import IconButton from '@components/buttons/icon-button'
import {
  AutoSwitchKeenSliderPlugin,
  getSlidesPerView,
  MutationKeenSliderPlugin,
  ResizeKeenSliderPlugin,
} from '@lib/keen-slider'
import { SanityContentCarouselItem } from '@data/sanity/queries/types/blocks'
import ContentCarouselItem from '@blocks/content-carousel-item'

interface CarouselProps {
  items: SanityContentCarouselItem[]
  options: KeenSliderOptions & { selector: string }
  showArrows?: boolean
  autoSwitch?: boolean
  autoSwitchTimeout?: number
  previewNextSlide?: boolean
  showOverflow?: boolean
  className?: string
}

const Carousel = ({
  items,
  options,
  showArrows,
  autoSwitch,
  autoSwitchTimeout,
  previewNextSlide,
  showOverflow,
  className,
}: CarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesPerView, setSlidesPerView] = useState(1)

  const keenSliderPlugins = useMemo(() => {
    const keenSliderPlugins: KeenSliderPlugin[] = [
      MutationKeenSliderPlugin,
      ResizeKeenSliderPlugin,
    ]

    if (autoSwitch) {
      keenSliderPlugins.push(
        AutoSwitchKeenSliderPlugin(autoSwitchTimeout ?? 2000),
      )
    }

    return keenSliderPlugins
  }, [autoSwitch, autoSwitchTimeout])

  const [ref, slider] = useKeenSlider<HTMLDivElement>(
    {
      defaultAnimation: {
        duration: 800,
      },
      slides: {
        perView: previewNextSlide && items.length > 1 ? 1.15 : 1,
        spacing: 20,
      },
      drag: deviceType === 'touchOnly',
      breakpoints: {
        [`(min-width: ${screens.sm})`]: {
          slides: {
            perView: previewNextSlide && items.length > 2 ? 2.15 : 2,
            spacing: 20,
          },
        },
        [`(min-width: ${screens.md})`]: {
          slides: {
            perView: previewNextSlide && items.length > 3 ? 3.15 : 3,
            spacing: 20,
          },
        },
        [`(min-width: ${screens.lg})`]: {
          slides: {
            perView: previewNextSlide && items.length > 4 ? 4.15 : 4,
            spacing: 20,
          },
        },
      },
      created: (slider) => {
        const newSlidesPerView = getSlidesPerView(slider)

        if (newSlidesPerView) {
          setSlidesPerView(newSlidesPerView)
        }
      },
      optionsChanged: (slider) => {
        const newSlidesPerView = getSlidesPerView(slider)

        if (newSlidesPerView) {
          setSlidesPerView(newSlidesPerView)
        }
      },
      slideChanged: (slider) => {
        setCurrentSlide(slider.track?.details?.rel)
      },
      ...options,
    },
    keenSliderPlugins,
  )

  return (
    <div className={cx(className, 'relative')}>
      <div
        ref={ref}
        className={cx(
          'flex relative will-change-transform touch-action touch-action-pan-y',
          {
            'overflow-hidden': !showOverflow,
          },
        )}
      >
        {items.map((item, index) => (
          <div key={index} className="content-slide">
            <ContentCarouselItem item={item} />
          </div>
        ))}
      </div>

      {showArrows && items.length > slidesPerView && (
        <>
          <div
            className={cx(
              'flex flex-col justify-center',
              'absolute inset-y-0 left-[-15px] z-10',
            )}
          >
            <IconButton
              type="button"
              icon="ArrowLeft"
              variant={ButtonVariant.FILLED}
              color={ButtonColor.GREEN_LIGHT}
              size={ButtonSize.LARGE}
              disabled={!options.loop && currentSlide === 0}
              onClick={() => {
                slider.current?.prev()
              }}
            />
          </div>

          <div
            className={cx(
              'flex flex-col justify-center',
              'absolute inset-y-0 right-[-15px] z-10',
            )}
          >
            <IconButton
              type="button"
              icon="ArrowRight"
              variant={ButtonVariant.FILLED}
              color={ButtonColor.GREEN_LIGHT}
              size={ButtonSize.LARGE}
              disabled={
                !options.loop && currentSlide >= items.length - slidesPerView
              }
              onClick={() => {
                slider.current?.next()
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default Carousel
